import { Button, DatePicker, Form, FormInstance, Input } from 'antd';
import { ReactComponent as Date } from 'assets/icons/Date.svg';
import CollapsibleCard from 'components/CollapsibleCard';
import { getFieldRules, requiredRule } from 'const/formRules';
import { useAuthContext } from 'contexts/AuthContext';
import { Dayjs } from 'dayjs';
import useFormSubmittable from 'hooks/useFormSubmittable';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddressFormFields, {
  AddressFormFieldsConfig,
} from 'views/dossiers/concessionnaire/steps/components/AddressFormFields';

export type SiteInformationsType = {
  interlocuteurChantierLastname: string;
  interlocuteurChantierFirstname: string;
  interlocuteurChantierEmail: string;
  raisonSocialeBeneficiaire: string;
  nomSite: string;
  adresseSite: string;
  codePostalSite: string;
  villeSite: string;
  dateDebutTravaux: Dayjs;
  dateFinTravaux: Dayjs;
};

export type SiteContactInformationsType = Pick<
  SiteInformationsType,
  | 'interlocuteurChantierLastname'
  | 'interlocuteurChantierFirstname'
  | 'interlocuteurChantierEmail'
  | 'raisonSocialeBeneficiaire'
>;

export type SiteAddressInformationsType = Pick<
  SiteInformationsType,
  | 'nomSite'
  | 'adresseSite'
  | 'codePostalSite'
  | 'villeSite'
  | 'dateDebutTravaux'
  | 'dateFinTravaux'
>;

const CONTACT_SECTION_FIELDS: (keyof SiteContactInformationsType)[] = [
  'interlocuteurChantierLastname',
  'interlocuteurChantierFirstname',
  'interlocuteurChantierEmail',
  'raisonSocialeBeneficiaire',
];

const ADDRESS_SECTION_FIELDS: (keyof SiteAddressInformationsType)[] = [
  'nomSite',
  'adresseSite',
  'codePostalSite',
  'villeSite',
  'dateDebutTravaux',
  'dateFinTravaux',
];

interface SiteInformationsFormProps {
  contactFormInstance: FormInstance<SiteContactInformationsType>;
  addressFormInstance: FormInstance<SiteAddressInformationsType>;
  onCompletionChange?: (isComplete: boolean) => void;
  initialIsComplete?: boolean;
  initialValues?: Partial<SiteInformationsType>;
  onValuesChange?: (
    changedValues: Partial<SiteInformationsType>,
    allValues: SiteInformationsType,
  ) => void;
}

export default function SiteInformationsForm({
  contactFormInstance,
  addressFormInstance,
  onCompletionChange,
  initialIsComplete = false,
  initialValues,
  onValuesChange,
}: SiteInformationsFormProps) {
  const { t } = useTranslation(['add_dossier_site', 'global']);

  const { auth } = useAuthContext();

  const siteAddressConfig: AddressFormFieldsConfig = useMemo(
    () => ({
      fieldNames: {
        address: 'adresseSite',
        postalCode: 'codePostalSite',
        city: 'villeSite',
      },
      labels: {
        address: t('adresseSite'),
        postalCode: t('codePostalSite'),
        city: t('villeSite'),
      },
      placeholders: {
        address: t('adresseSite_placeholder'),
        postalCode: t('codePostalSite_placeholder'),
        city: t('villeSite_placeholder'),
      },
      validateDepartmentCode: false,
      queryKey: 'site-addresses',
    }),
    [t],
  );

  const [contactSectionSaved, setContactSectionSaved] =
    useState(initialIsComplete);
  const [addressSectionSaved, setAddressSectionSaved] =
    useState(initialIsComplete);
  const [isContactOpen, setIsContactOpen] = useState(!initialIsComplete);
  const [isAddressOpen, setIsAddressOpen] = useState(false);

  const dateDebutTravaux = Form.useWatch(
    'dateDebutTravaux',
    addressFormInstance,
  );
  const maxDate = Form.useWatch('dateFinTravaux', addressFormInstance);
  const isContactSectionSubmittable = useFormSubmittable(contactFormInstance);
  const isAddressSectionSubmittable = useFormSubmittable(addressFormInstance);

  const contactInitialValues = useMemo<
    Partial<SiteContactInformationsType>
  >(() => {
    // Si initialValues est fourni (même vide), il prime sur les valeurs auth.
    if (initialValues !== undefined) {
      return {
        interlocuteurChantierLastname:
          initialValues.interlocuteurChantierLastname,
        interlocuteurChantierFirstname:
          initialValues.interlocuteurChantierFirstname,
        interlocuteurChantierEmail: initialValues.interlocuteurChantierEmail,
        raisonSocialeBeneficiaire: initialValues.raisonSocialeBeneficiaire,
      };
    }

    return {
      interlocuteurChantierLastname: auth?.user?.lastname || '',
      interlocuteurChantierFirstname: auth?.user?.firstname || '',
      interlocuteurChantierEmail: auth?.user?.email || '',
      raisonSocialeBeneficiaire: undefined,
    };
  }, [
    auth?.user?.email,
    auth?.user?.firstname,
    auth?.user?.lastname,
    initialValues,
  ]);

  const addressInitialValues = useMemo<Partial<SiteAddressInformationsType>>(
    () => ({
      nomSite: initialValues?.nomSite,
      adresseSite: initialValues?.adresseSite,
      codePostalSite: initialValues?.codePostalSite,
      villeSite: initialValues?.villeSite,
      dateDebutTravaux: initialValues?.dateDebutTravaux,
      dateFinTravaux: initialValues?.dateFinTravaux,
    }),
    [initialValues],
  );

  const isComplete = contactSectionSaved && addressSectionSaved;

  useEffect(() => {
    onCompletionChange?.(isComplete);
  }, [isComplete, onCompletionChange]);

  const getMergedSiteValues = (): SiteInformationsType =>
    ({
      ...contactFormInstance.getFieldsValue(true),
      ...addressFormInstance.getFieldsValue(true),
    }) as SiteInformationsType;

  const handleSaveContactSection = async () => {
    try {
      await contactFormInstance.validateFields(
        CONTACT_SECTION_FIELDS as string[],
      );
    } catch {
      return;
    }

    setContactSectionSaved(true);
    setIsContactOpen(false);
    setIsAddressOpen(true);
  };

  const handleSaveAddressSection = async () => {
    try {
      await addressFormInstance.validateFields(
        ADDRESS_SECTION_FIELDS as string[],
      );
    } catch {
      return;
    }

    setAddressSectionSaved(true);
    setIsAddressOpen(false);
  };

  const handleContactValuesChange = (
    changedValues: Partial<SiteContactInformationsType>,
  ) => {
    if (contactSectionSaved && Object.keys(changedValues).length > 0) {
      setContactSectionSaved(false);
    }

    onValuesChange?.(
      changedValues as Partial<SiteInformationsType>,
      getMergedSiteValues(),
    );
  };

  const handleAddressValuesChange = (
    changedValues: Partial<SiteAddressInformationsType>,
  ) => {
    if (addressSectionSaved && Object.keys(changedValues).length > 0) {
      setAddressSectionSaved(false);
    }

    onValuesChange?.(
      changedValues as Partial<SiteInformationsType>,
      getMergedSiteValues(),
    );
  };

  return (
    <>
      <div className="space-y-4">
        <CollapsibleCard
          title={t('coordonnees_interlocuteur')}
          isOpen={isContactOpen}
          isComplete={contactSectionSaved}
          onToggle={
            contactSectionSaved
              ? () => setIsContactOpen((prev) => !prev)
              : undefined
          }
        >
          <Form<SiteContactInformationsType>
            form={contactFormInstance}
            layout={'vertical'}
            onValuesChange={handleContactValuesChange}
            initialValues={contactInitialValues}
          >
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <Form.Item<SiteContactInformationsType>
                  name="interlocuteurChantierLastname"
                  label={t('nom')}
                  required
                  rules={getFieldRules(t, { required: true, text_only: true })}
                  className="mb-0"
                >
                  <Input placeholder={t('nom_placeholder')} />
                </Form.Item>
                <Form.Item<SiteContactInformationsType>
                  name="interlocuteurChantierFirstname"
                  label={t('prenom')}
                  required
                  rules={getFieldRules(t, { required: true, text_only: true })}
                  className="mb-0"
                >
                  <Input placeholder={t('prenom_placeholder')} />
                </Form.Item>
              </div>
              <Form.Item<SiteContactInformationsType>
                name="interlocuteurChantierEmail"
                label={t('email')}
                required
                rules={getFieldRules(t, { required: true, email: true })}
                className="mb-0"
              >
                <Input placeholder={t('email_placeholder')} />
              </Form.Item>
              <Form.Item<SiteContactInformationsType>
                name="raisonSocialeBeneficiaire"
                label={t('raisonSocialeBeneficiaire')}
                required
                rules={[requiredRule]}
                className="mb-0"
              >
                <Input placeholder={t('raison_sociale_placeholder')} />
              </Form.Item>
              <Button
                type="primary"
                htmlType="button"
                onClick={handleSaveContactSection}
                disabled={!isContactSectionSubmittable}
                shape="round"
              >
                {t('global:enregistrer_et_continuer')}
              </Button>
            </div>
          </Form>
        </CollapsibleCard>

        <CollapsibleCard
          title={t('adresse_et_dates')}
          isOpen={isAddressOpen}
          showContent={contactSectionSaved}
          isComplete={addressSectionSaved}
          onToggle={
            addressSectionSaved
              ? () => setIsAddressOpen((prev) => !prev)
              : undefined
          }
        >
          <Form<SiteAddressInformationsType>
            form={addressFormInstance}
            layout={'vertical'}
            onValuesChange={handleAddressValuesChange}
            initialValues={addressInitialValues}
          >
            <div className="space-y-4 mt-4">
              <Form.Item<SiteAddressInformationsType>
                name="nomSite"
                label={t('nomSite')}
                required
                rules={[requiredRule]}
                className="mb-0"
              >
                <Input placeholder={t('nomSite_placeholder')} />
              </Form.Item>

              <AddressFormFields config={siteAddressConfig} />

              <div className="md:max-w-80 flex flex-col gap-4">
                <Form.Item<SiteAddressInformationsType>
                  name="dateDebutTravaux"
                  label={t('dateDebutTravaux')}
                  required
                  rules={[requiredRule]}
                  className="mb-0"
                >
                  <DatePicker
                    format={'DD/MM/YYYY'}
                    suffixIcon={<Date />}
                    className={'w-full'}
                    placeholder={t('global:select_date')}
                    maxDate={maxDate}
                  />
                </Form.Item>
                <Form.Item<SiteAddressInformationsType>
                  required
                  name={'dateFinTravaux'}
                  label={t('dateFinTravaux')}
                  rules={[requiredRule]}
                  className="mb-0"
                >
                  <DatePicker
                    format={'DD/MM/YYYY'}
                    suffixIcon={<Date />}
                    className={'w-full'}
                    placeholder={t('global:select_date')}
                    minDate={dateDebutTravaux}
                  />
                </Form.Item>
              </div>
              <Button
                type="primary"
                htmlType="button"
                onClick={handleSaveAddressSection}
                disabled={!isAddressSectionSubmittable}
                shape="round"
              >
                {t('global:enregistrer_et_continuer')}
              </Button>
            </div>
          </Form>
        </CollapsibleCard>
      </div>
    </>
  );
}
